import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Printer, Star, Upload, X, Loader2, Package, CheckCircle2, MessageSquare, Handshake, Info } from 'lucide-react';
const PaymentModal = lazy(() => import('./PaymentModal'));

const BACKEND = RUNTIME_BACKEND_URL;

export default function PrintOrderDialog({ open, onOpenChange, customer, album, token, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [albumcus, setAlbumcus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAlbumcu, setSelectedAlbumcu] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100
  const [uploadSpeed, setUploadSpeed] = useState(0);  // bytes/sec
  const [uploadEta, setUploadEta] = useState(null); // saniye
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [showReviewFor, setShowReviewFor] = useState(null);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedAlbumcu(null);
    setSelectedProduct(null);
    setSelectedVariantIds([]);
    setFiles([]);
    setNotes('');
    loadPartners();
  }, [open]);

  const loadPartners = async () => {
    setLoading(true);
    try {
      // Sadece anlaşmalı albümcüler - anlaşma yoksa liste boş gelir
      const res = await fetch(`${BACKEND}/api/albumcus/my-partners`, { headers: authHeader });
      const data = await res.json();
      setAlbumcus(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Albümcüler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const selectAlbumcu = async (a) => {
    setSelectedAlbumcu(a);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/albumcu/products/${a.id}`, { headers: authHeader });
      const data = await res.json();
      setProducts(data.products || []);
      setStep(2);
    } catch {
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...list]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!selectedProduct) return toast.error('Ürün seçin');
    if (files.length === 0) return toast.error('En az 1 dosya yükleyin');
    setSubmitting(true);
    setUploadProgress(0);
    setUploadSpeed(0);
    setUploadEta(null);
    setUploadedBytes(0);
    setTotalBytes(0);
    try {
      const fd = new FormData();
      fd.append('albumcu_id', selectedAlbumcu.id);
      fd.append('product_id', selectedProduct.id);
      fd.append('customer_id', customer?.id || '');
      fd.append('album_id', album?.id || '');
      // Müşterinin albüm için onayladığı isimler ve tarih varsa onları kullan
      const defaultNames = `${customer?.bride_name || ''} & ${customer?.groom_name || ''}`.trim().replace(/^& |& $/, '');
      const finalNames = (album?.album_names && album.album_names.trim()) || defaultNames;
      const finalDate = album?.album_date || customer?.wedding_date || '';
      let finalBride = '';
      let finalGroom = '';
      if (finalNames.includes('&')) {
        const parts = finalNames.split('&').map(s => s.trim()).filter(Boolean);
        finalBride = parts[0] || '';
        finalGroom = parts[1] || '';
      }
      fd.append('customer_name', finalNames);
      fd.append('bride_name', finalBride);
      fd.append('groom_name', finalGroom);
      fd.append('wedding_date', finalDate);
      fd.append('notes', notes);
      fd.append('variant_ids', selectedVariantIds.join(','));
      files.forEach(f => fd.append('files', f));

      // XHR ile upload progress
      const createdOrder = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND}/api/print-orders`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        const startedAt = Date.now();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
            setUploadedBytes(e.loaded);
            setTotalBytes(e.total);
            const elapsedSec = (Date.now() - startedAt) / 1000;
            if (elapsedSec > 0.3) {
              const speed = e.loaded / elapsedSec;  // B/s
              setUploadSpeed(speed);
              if (speed > 0) {
                setUploadEta(Math.max(0, Math.round((e.total - e.loaded) / speed)));
              }
            }
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Geçersiz yanıt')); }
          } else {
            reject(new Error(`Hata: HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Ağ hatası'));
        xhr.onabort = () => reject(new Error('İşlem iptal edildi'));
        xhr.send(fd);
      });

      toast.success('Sipariş oluşturuldu - şimdi ödeme ekranı açılıyor');
      setCreatedOrderId(createdOrder.id);
      setShowPayment(true);
    } catch (e) {
      toast.error('Sipariş gönderilemedi: ' + (e.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  // Yardımcı formatlamalar
  const formatBytes = (b) => {
    if (b > 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    if (b > 1024) return (b / 1024).toFixed(0) + ' KB';
    return b + ' B';
  };
  const formatEta = (sec) => {
    if (sec === null || sec === undefined) return '';
    if (sec < 60) return `~${sec}sn`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `~${m}dk ${s}sn`;
  };

  const sendReview = async () => {
    if (!showReviewFor || !myRating) return;
    try {
      await fetch(`${BACKEND}/api/albumcu-reviews/${showReviewFor.id}`, {
        method: 'POST', headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: myRating, comment: myComment })
      });
      toast.success('Puanlamanız kaydedildi');
      setShowReviewFor(null);
      setMyRating(0);
      setMyComment('');
      loadPartners();
    } catch { toast.error('Hata'); }
  };

  const goToTemplates = () => {
    onOpenChange(false);
    navigate('/panel/album-sablonlari?tab=albumcus');
  };

  const minPrice = (p) => (p.price || 0) + ((p.variants || []).length > 0 ? Math.min(...(p.variants || []).map(v => v.price || 0)) : 0);
  const totalPrice = selectedProduct
    ? (selectedProduct.price || 0) + (selectedProduct.variants || [])
        .filter(v => selectedVariantIds.includes(v.id))
        .reduce((s, v) => s + (v.price || 0), 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="print-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-primary" />
            Albüm Baskısı İçin Gönder
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs font-medium">
          {['Albümcü', 'Ürün', 'Dosyalar'].map((label, i) => {
            const n = i + 1;
            return (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  step >= n ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'
                }`}>{n}</div>
                <span className={step >= n ? 'text-primary' : 'text-slate-500'}>{label}</span>
                {n < 3 && <div className="w-8 h-px bg-slate-200 mx-1" />}
              </div>
            );
          })}
        </div>

        {/* STEP 1: Anlaşmalı albümcüler */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-emerald-800">
                Bu listede <b>yalnızca anlaşmalı olduğunuz albümcüler</b> görünür.
                Yeni bir albümcüyle anlaşma yapmak için <b>Albüm Şablonları → Albümcüler</b> sayfasına gidin.
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : albumcus.length === 0 ? (
              <div className="p-8 text-center border-2 border-dashed rounded-xl space-y-3" data-testid="no-partners-empty">
                <Handshake className="h-10 w-10 text-slate-300 mx-auto" />
                <div>
                  <p className="font-bold text-sm">Henüz anlaşmalı albümcünüz yok</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Sipariş verebilmek için önce bir albümcüyle anlaşma yapmalısınız.
                  </p>
                </div>
                <Button onClick={goToTemplates} data-testid="goto-albumcus-btn"
                  className="bg-primary hover:bg-primary/90">
                  <Handshake className="h-4 w-4 mr-2" />
                  Albümcülere Git & Anlaşma Yap
                </Button>
              </div>
            ) : (
              <div className="grid gap-2">
                {albumcus.map((a) => (
                  <div key={a.id} className="border rounded-xl p-3 flex items-center justify-between hover:border-primary transition"
                    data-testid={`albumcu-card-${a.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                          <CheckCircle2 className="h-3 w-3" />Anlaşmalı
                        </span>
                        <Package className="h-4 w-4 text-primary" />
                        <span className="font-bold text-sm">{a.company_name || a.full_name}</span>
                        {a.rating > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600">
                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                            {a.rating.toFixed(1)}
                            <span className="text-slate-400 font-normal">({a.review_count})</span>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {a.full_name} · {a.phone || '-'} · {a.product_count || 0} ürün
                        {a.completed_orders > 0 && <span className="text-emerald-600 font-medium"> · {a.completed_orders} tamamlanan</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" title="Puanla" onClick={() => { setShowReviewFor(a); setMyRating(0); setMyComment(''); }}
                        data-testid={`rate-${a.id}`}>
                        <Star className="h-4 w-4 text-amber-500" />
                      </Button>
                      <Button size="sm" onClick={() => selectAlbumcu(a)}
                        disabled={!a.product_count}
                        data-testid={`select-albumcu-${a.id}`}
                        className="bg-emerald-600 hover:bg-emerald-700">
                        Sipariş Ver
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Ürün seç */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-600">
                  <span className="font-bold">{selectedAlbumcu?.company_name}</span>
                  <span className="ml-2 text-xs text-emerald-600 font-medium">✓ Anlaşmalı</span>
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setStep(1)}>← Albümcü Değiştir</Button>
            </div>

            {products.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Bu albümcünün ürünü yok</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {products.map(p => {
                  const mp = minPrice(p);
                  const hasVariants = (p.variants || []).length > 0;
                  return (
                    <div key={p.id}
                      onClick={() => { setSelectedProduct(p); setSelectedVariantIds([]); setStep(3); }}
                      className={`border rounded-xl p-3 cursor-pointer transition hover:border-primary ${
                        selectedProduct?.id === p.id ? 'border-primary bg-primary/5' : ''
                      }`} data-testid={`product-card-${p.id}`}>
                      {p.sample_images?.[0] ? (
                        <img src={`${BACKEND}/api/albumcu-samples/${p.sample_images[0]}`} alt=""
                          className="w-full h-32 object-cover rounded-lg mb-2" />
                      ) : (
                        <div className="w-full h-32 bg-slate-100 rounded-lg mb-2 flex items-center justify-center">
                          <Package className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                      <div className="font-bold text-sm">{p.name}</div>
                      <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{p.description}</div>
                      <div className="mt-2">
                        {hasVariants ? (
                          <>
                            <div className="text-[10px] text-slate-400">başlangıç fiyatı</div>
                            <div className="text-lg font-bold text-emerald-600">
                              {mp.toLocaleString('tr-TR')} TL <span className="text-xs font-normal text-slate-500">'den başlayan</span>
                            </div>
                            <div className="text-[10px] text-primary mt-0.5">{p.variants.length} seçenek</div>
                          </>
                        ) : (
                          <div className="text-lg font-bold text-emerald-600">
                            {p.price?.toLocaleString('tr-TR')} TL
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Dosya yükle & gönder */}
        {step === 3 && selectedProduct && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs text-slate-500">Seçilen ürün</p>
                <p className="font-bold text-sm">{selectedAlbumcu?.company_name} · {selectedProduct.name}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setStep(2)}>← Ürün Değiştir</Button>
            </div>

            {(selectedProduct.variants || []).length > 0 && (
              <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3">
                <p className="text-xs font-bold text-primary mb-2">Seçenekleri işaretleyin:</p>
                <div className="space-y-1">
                  {selectedProduct.variants.map(v => {
                    const checked = selectedVariantIds.includes(v.id);
                    return (
                      <label key={v.id} className="flex items-center gap-2 cursor-pointer bg-white border rounded-lg p-2 hover:border-primary">
                        <input type="checkbox" checked={checked}
                          onChange={e => {
                            setSelectedVariantIds(prev => e.target.checked ? [...prev, v.id] : prev.filter(x => x !== v.id));
                          }}
                          data-testid={`variant-check-${v.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{v.name}</div>
                          {v.description && <div className="text-[11px] text-slate-500">{v.description}</div>}
                        </div>
                        <span className="text-sm font-bold text-emerald-600">
                          {v.price > 0 ? '+' : ''}{v.price.toLocaleString('tr-TR')} TL
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] text-slate-500">Toplam Fiyat</div>
                <div className="text-2xl font-bold text-emerald-700">{totalPrice.toLocaleString('tr-TR')} TL</div>
              </div>
              <div className="text-[10px] text-slate-500 text-right">
                Ana fiyat: {selectedProduct.price?.toLocaleString('tr-TR')} TL<br />
                {selectedVariantIds.length > 0 && `+ ${selectedVariantIds.length} seçenek`}
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
              {(() => {
                const defaultNames = `${customer?.bride_name || ''} & ${customer?.groom_name || ''}`.trim().replace(/^& |& $/, '');
                const editedNames = album?.album_names && album.album_names.trim();
                const finalNames = editedNames || defaultNames;
                const editedDate = album?.album_date;
                const finalDate = editedDate || customer?.wedding_date;
                const namesEdited = editedNames && editedNames !== defaultNames;
                const dateEdited = editedDate && editedDate !== customer?.wedding_date;
                return (
                  <>
                    <div>
                      <b>Albüm İsimleri:</b> {finalNames}
                      {namesEdited && <span className="ml-1 text-emerald-600 text-[10px] font-bold">(müşteri düzenledi)</span>}
                    </div>
                    {finalDate && (
                      <div>
                        <b>Tarih:</b> {new Date(finalDate).toLocaleDateString('tr-TR')}
                        {dateEdited && <span className="ml-1 text-emerald-600 text-[10px] font-bold">(müşteri düzenledi)</span>}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div>
              <Label>Albüm Dosyalarını Yükleyin *</Label>
              <p className="text-xs text-slate-500 mb-2">PDF, JPG veya işlenmiş resim dosyalarınızı seçin</p>
              <input type="file" multiple onChange={handleFiles}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-white"
                data-testid="print-files-input" />
              {files.length > 0 && (
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border rounded p-2 text-xs">
                      <span className="truncate">{f.name} ({(f.size/1024/1024).toFixed(1)} MB)</span>
                      <button onClick={() => removeFile(i)} className="text-red-500">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Albümcüye Not (opsiyonel)</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Örn: Kapak deri kaplı olsun, sayfa numarası eklenmesin..."
                rows={3} data-testid="print-notes-input" />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 flex items-start gap-2 text-xs">
              <Info className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-amber-800">
                Kargo için adres bilgilerinizin güncel olduğundan emin olun. <b>Ayarlar → Profil</b> bölümünden kontrol edebilirsiniz.
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              {submitting ? (
                <div className="flex-1">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-bold text-emerald-700 flex items-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Yükleniyor... {uploadProgress}%
                    </span>
                    <span className="text-slate-500">
                      {totalBytes > 0 && (
                        <>{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}
                          {uploadSpeed > 0 && <span className="ml-1.5">• {formatBytes(uploadSpeed)}/sn</span>}
                          {uploadEta !== null && <span className="ml-1.5">• {formatEta(uploadEta)}</span>}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden" data-testid="upload-progress-bar">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-200 relative overflow-hidden"
                      style={{ width: `${uploadProgress}%` }}>
                      <div className="absolute inset-0 bg-white/20 animate-pulse" />
                    </div>
                  </div>
                  {uploadProgress >= 100 && (
                    <p className="text-[10px] text-emerald-700 mt-1">Yükleme tamamlandı, sunucu işliyor...</p>
                  )}
                </div>
              ) : (
                <>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
                  <Button onClick={submit} disabled={files.length === 0}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-print-order-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    {totalPrice.toLocaleString('tr-TR')} TL'e Gönder
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Review Dialog */}
        {showReviewFor && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowReviewFor(null)}>
            <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-5 w-5 text-primary" />
                <h3 className="font-bold">{showReviewFor.company_name} - Puanlama</h3>
              </div>
              <div className="flex justify-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setMyRating(n)} data-testid={`star-${n}`}>
                    <Star className={`h-8 w-8 transition-all ${n <= myRating ? 'fill-amber-400 text-amber-400 scale-110' : 'text-slate-300'}`} />
                  </button>
                ))}
              </div>
              <Textarea value={myComment} onChange={e => setMyComment(e.target.value)}
                placeholder="Yorumunuz (opsiyonel)..." rows={3} />
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setShowReviewFor(null)}>İptal</Button>
                <Button onClick={sendReview} disabled={!myRating} data-testid="submit-review-btn">Kaydet</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Payment Modal */}
      <Suspense fallback={null}>
        {showPayment && (
          <PaymentModal
            open={showPayment}
            onOpenChange={(o) => {
              setShowPayment(o);
              if (!o) {
                onCreated && onCreated();
                onOpenChange(false);
              }
            }}
            flow="photographer_to_albumcu"
            amount={totalPrice}
            title={`Albüm Baskı - ${customer?.bride_name || ''} & ${customer?.groom_name || ''}`}
            recipientId={selectedAlbumcu?.id}
            meta={{ print_order_id: createdOrderId, customer_id: customer?.id, album_id: album?.id }}
            token={token}
            onSuccess={() => {
              toast.success('Sipariş ödendi ve albümcüye gönderildi!');
              onCreated && onCreated();
              onOpenChange(false);
            }}
          />
        )}
      </Suspense>
    </Dialog>
  );
}
